import { useCallback, useMemo } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useRoutePlanner } from '@/hooks/use-route-planner';
import { RouteMapPinBuilder } from '@/lib/route-plan/RouteMapPinBuilder';
import { RouteMapView } from '@/components/route-planner/map/RouteMapView';
import { StopPickerStep } from '@/components/route-planner/StopPickerStep';
import { LocationsStep } from '@/components/route-planner/LocationsStep';
import { RouteReviewTimeline } from '@/components/route-planner/RouteReviewTimeline';

/**
 * Route planner wizard: pick stops → confirm locations → review route.
 */
export default function RoutePlannerScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const router = useRouter();
  const planner = useRoutePlanner();

  // Static route-shape preview for the review step: no current step yet.
  const reviewMapModel = useMemo(
    () =>
      RouteMapPinBuilder.build({
        stops: planner.draftStops,
        legs: planner.draftLegs,
        completedLegIds: [],
        currentLegIndex: -1,
      }),
    [planner.draftStops, planner.draftLegs],
  );

  const handleWindowChange = useCallback(
    (start: string | null, end: string | null) => {
      planner.setWindowStart(start);
      planner.setWindowEnd(end);
    },
    [planner],
  );

  const handleSave = useCallback(async () => {
    const plan = await planner.saveRoute();
    if (plan) {
      router.dismissAll();
      router.replace('/');
    }
  }, [planner, router]);

  const canContinueFromPick = planner.selectedCandidates.length > 0;
  const canBuild = planner.homeAddress.trim().length > 0;

  const saveLabel = planner.remindersAvailable && planner.travelLegCount > 0
    ? `Save route · ${planner.travelLegCount} reminders`
    : 'Save route';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['bottom']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {planner.step === 'pick' && (
            <StopPickerStep
              date={planner.date}
              onDateChange={planner.setDate}
              windowStart={planner.windowStart}
              windowEnd={planner.windowEnd}
              onWindowChange={handleWindowChange}
              eventCandidates={planner.eventCandidates}
              noteCandidates={planner.noteCandidates}
              todoCandidates={planner.todoCandidates}
              eventsLoading={planner.eventsLoading}
              scanStatus={planner.scanStatus}
              scanError={planner.scanError}
              onRetryScan={planner.rescan}
              familyMembers={planner.familyMembers}
              onToggle={planner.toggleCandidate}
              onTimeChange={planner.setCandidateStart}
            />
          )}

          {planner.step === 'locations' && (
            <LocationsStep
              homeAddress={planner.homeAddress}
              onHomeAddressChange={planner.setHomeAddress}
              selectedCandidates={planner.selectedCandidates}
              onLocationChange={planner.setCandidateLocation}
            />
          )}

          {planner.step === 'review' && (
            <>
              {planner.buildStatus === 'loading' && (
                <View style={styles.buildState}>
                  <ActivityIndicator color={palette.primary} />
                  <Text style={[styles.buildText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                    Building your route…
                  </Text>
                </View>
              )}
              {planner.buildStatus === 'error' && (
                <View style={styles.buildState}>
                  <Text style={[styles.buildText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                    {planner.buildError ?? 'Could not build the route.'}
                  </Text>
                  <TouchableOpacity onPress={planner.buildRoute}>
                    <Text style={[styles.retryText, { color: palette.primary, fontFamily: getBodyFont() }]}>
                      Retry
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
              {planner.buildStatus === 'done' && (
                <>
                  <RouteMapView model={reviewMapModel} interactive={false} height={110} />
                  <RouteReviewTimeline
                    stops={planner.draftStops}
                    legs={planner.draftLegs}
                    familyMembers={planner.familyMembers}
                    onSetDuration={planner.setTravelDurationMinutes}
                    onDeleteLeg={planner.deleteLeg}
                    onChangeMode={planner.changeTravelMode}
                  />
                </>
              )}
            </>
          )}
        </ScrollView>

        {/* Footer navigation */}
        <View style={[styles.footer, { borderTopColor: palette.borderMuted }]}>
          {planner.step !== 'pick' && (
            <TouchableOpacity
              style={[styles.secondaryButton, { borderColor: palette.border }]}
              onPress={() => planner.setStep(planner.step === 'review' ? 'locations' : 'pick')}
            >
              <Text style={[styles.secondaryLabel, { color: palette.text, fontFamily: getBodyFont() }]}>
                Back
              </Text>
            </TouchableOpacity>
          )}

          {planner.step === 'pick' && (
            <TouchableOpacity
              style={[
                styles.primaryButton,
                { backgroundColor: palette.primary },
                !canContinueFromPick && styles.disabledButton,
              ]}
              disabled={!canContinueFromPick}
              onPress={() => planner.setStep('locations')}
            >
              <Text style={[styles.primaryLabel, { fontFamily: getBodyFont() }]}>Continue</Text>
            </TouchableOpacity>
          )}

          {planner.step === 'locations' && (
            <TouchableOpacity
              style={[
                styles.primaryButton,
                { backgroundColor: palette.primary },
                !canBuild && styles.disabledButton,
              ]}
              disabled={!canBuild}
              onPress={planner.buildRoute}
            >
              <Text style={[styles.primaryLabel, { fontFamily: getBodyFont() }]}>Build route</Text>
            </TouchableOpacity>
          )}

          {planner.step === 'review' && (
            <TouchableOpacity
              style={[
                styles.primaryButton,
                { backgroundColor: palette.primary },
                (planner.buildStatus !== 'done' || planner.isSaving) && styles.disabledButton,
              ]}
              disabled={planner.buildStatus !== 'done' || planner.isSaving}
              onPress={handleSave}
            >
              {planner.isSaving ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={[styles.primaryLabel, { fontFamily: getBodyFont() }]}>{saveLabel}</Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContent: {
    padding: Spacing.lg,
    paddingBottom: Spacing.xxl,
    gap: Spacing.md,
  },
  buildState: {
    alignItems: 'center',
    gap: Spacing.md,
    paddingVertical: Spacing.xxl,
  },
  buildText: {
    fontSize: 14,
    textAlign: 'center',
  },
  retryText: {
    fontSize: 14,
    fontWeight: '600',
  },
  footer: {
    flexDirection: 'row',
    gap: Spacing.md,
    padding: Spacing.lg,
    borderTopWidth: 1,
  },
  primaryButton: {
    flex: 1,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryLabel: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  secondaryButton: {
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.xl,
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryLabel: {
    fontSize: 15,
    fontWeight: '600',
  },
  disabledButton: {
    opacity: 0.4,
  },
});
